/**
 * NOS-248: Public-site CDN cache — policy + invalidation entry point.
 *
 * Builder/dashboard code calls `queuePublicCachePurge(orgId, trigger)` after a
 * public-content write. The trigger is mapped to a purge scope (purgeTriggers),
 * debounced/coalesced per org (invalidationQueue), then sent to the
 * `invalidate-public-cache` edge function, which owns the CDN provider call.
 */
import { supabase } from "@/lib/supabase"
import { planPurge, type PurgeTrigger } from "./purgeTriggers"
import { createPurgeQueue, type PurgeQueue } from "./invalidationQueue"

export {
  classifyPublicRoute,
  getCacheControl,
  getPublicCacheHeaders,
  isNeverCachePath,
  normalizePublicPath,
  orgCacheTag,
  pagePathForSlug,
  pathCacheTag,
  NEVER_CACHE_CONTROL,
  ROUTE_TTLS,
  type PublicRouteType,
} from "./cachePolicy"
export { planPurge, type PurgeTrigger, type PurgePlan } from "./purgeTriggers"
export { createPurgeQueue, type PurgeQueue, type PurgeFlusher } from "./invalidationQueue"

let queue: PurgeQueue | null = null

function getQueue(): PurgeQueue {
  if (!queue) {
    queue = createPurgeQueue(async (orgId, plan) => {
      const { error } = await supabase.functions.invoke("invalidate-public-cache", {
        body: {
          org_id: orgId,
          scope: plan.scope,
          ...(plan.scope === "paths" ? { paths: plan.paths } : {}),
        },
      })
      if (error) throw error
    })
  }
  return queue
}

/**
 * Queue a CDN purge for a public-content change. Fire-and-forget by design:
 * never awaited by save paths and never throws — a lost purge only leaves
 * content stale until its s-maxage expires.
 */
export function queuePublicCachePurge(orgId: string | null | undefined, trigger: PurgeTrigger): void {
  if (!orgId) return
  try {
    getQueue().enqueue(orgId, planPurge(trigger))
  } catch (err) {
    console.warn("Failed to queue cache purge:", err)
  }
}

/** Test hook: reset the singleton queue (e.g. between vitest cases). */
export function __resetPurgeQueueForTests(): void {
  queue = null
}
